import { httpClient, ApiResponse } from '@/utils';
import {
  ProductsEndpoints,
  IGenerateNamePreviewResponse,
  ITranslateFieldResponse,
  IProductListResponse,
  IGetProductsListDTO,
  IDeleteBulkProductsDTO,
  IGetProductResponse,
  IToggleActiveBulkProductDTO,
  IUpdateProductDTO,
  IUpdateProductResponse,
  IGetFiltersByCategoryResponse,
  IGetProductVariationsResponse,
  IBulkSearchProductVariationsDTO,
  IBulkSearchProductVariationsResponse,
  IAddProductVariationsBulkDTO,
  IUpdateProductVariationDto,
  IUpdateProductSeoDTO,
  IGetProductRegionsAndPricesResponse,
  IUpdateRegionsAndPricesItemDTO,
  IUpdateRegionsAndPricesItemResponse,
  IBulkUpdateStocksDTO,
  IBulkUpdateStocksResponse,
  IGetProductsOfMonthListDTO,
  IUpdateProductsOfMonthDTO,
  IGetStoreListDTO,
  IStoreResponse,
  ICreateProductDTO,
  ITopProductListResponse,
  IUpdateTopProductsDTO,
  IGetTopProductResponse,
  IReorderTopProductsDTO,
} from '@/models/products.model';
import convertModelToFormData from '../shared/utils/convertModelToFormData';

class ProductsAPI {
  static getProductsList(data: IGetProductsListDTO): ApiResponse<IProductListResponse> {
    return httpClient.get<IProductListResponse>(ProductsEndpoints.PRODUCTS, { params: data });
  }

  static getProductsOfMonthList(data: IGetProductsOfMonthListDTO): ApiResponse<IProductListResponse> {
    return httpClient.get<IProductListResponse>(ProductsEndpoints.MONTH_PRODUCTS, { params: data });
  }

  static getTopProductList(): ApiResponse<ITopProductListResponse> {
    return httpClient.get<ITopProductListResponse>(ProductsEndpoints.TOP_PRODUCTS);
  }

  static deleteProductsOfMonth(id: string) {
    return httpClient.delete(`${ProductsEndpoints.MONTH_PRODUCTS}/${id}`);
  }

  static deleteTopProducts(id: string) {
    return httpClient.delete(`${ProductsEndpoints.TOP_PRODUCTS}/${id}`);
  }

  static createProductsOfMonth(params: IUpdateProductsOfMonthDTO): ApiResponse<IGetProductResponse> {
    return httpClient.post<IGetProductResponse>(`${ProductsEndpoints.MONTH_PRODUCTS}`, params);
  }

  static createTopProduct(params: IUpdateTopProductsDTO): ApiResponse<IGetTopProductResponse> {
    return httpClient.post<IGetTopProductResponse>(`${ProductsEndpoints.TOP_PRODUCTS}`, params);
  }

  static reorderTopProduct(data: IReorderTopProductsDTO): ApiResponse<IGetTopProductResponse> {
    return httpClient.patch<IGetTopProductResponse>(`${ProductsEndpoints.TOP_PRODUCTS}/reorder`, data);
  }

  static updateProductsOfMonth({
    id,
    params,
  }: {
    id: string;
    params: IUpdateProductsOfMonthDTO;
  }): ApiResponse<IGetProductResponse> {
    return httpClient.patch<IGetProductResponse>(`${ProductsEndpoints.MONTH_PRODUCTS}/${id}`, params);
  }

  static updateTopProduct({
    id,
    params,
  }: {
    id: string;
    params: IUpdateTopProductsDTO;
  }): ApiResponse<IGetTopProductResponse> {
    return httpClient.patch<IGetTopProductResponse>(`${ProductsEndpoints.TOP_PRODUCTS}/${id}`, params);
  }

  static getProduct(id: string): ApiResponse<IGetProductResponse> {
    return httpClient.get<IGetProductResponse>(`${ProductsEndpoints.PRODUCTS}/${id}`);
  }

  static getFiltersByCategory(categoryId: string): ApiResponse<IGetFiltersByCategoryResponse> {
    return httpClient.get<IGetFiltersByCategoryResponse>(ProductsEndpoints.PRODUCT_FILTERS_BY_CATEGORY, {
      params: { category_id: categoryId },
    });
  }

  static getProductFiltersByCategory(data: {
    productId: string;
    categoryId: string;
  }): ApiResponse<IGetFiltersByCategoryResponse> {
    return httpClient.get<IGetFiltersByCategoryResponse>(
      `${ProductsEndpoints.PRODUCTS}/${data.productId}/filters-by-category`,
      {
        params: { category_id: data.categoryId },
      },
    );
  }

  static updateProductFilters({
    productId,
    params,
  }: {
    productId: string;
    params: Record<string, string>;
  }): ApiResponse<IGetProductResponse> {
    return httpClient.patch(`${ProductsEndpoints.PRODUCTS}/${productId}/filters-values`, undefined, { params });
  }

  static deleteProduct(id: string) {
    return httpClient.delete(`${ProductsEndpoints.PRODUCTS}/${id}`);
  }

  static deleteBulkProduct(data: IDeleteBulkProductsDTO) {
    const { all, ids, ...rest } = data;
    return httpClient.delete(ProductsEndpoints.PRODUCTS, {
      params: all ? { all: '1', ...rest } : { ids, ...rest },
    });
  }

  static toggleActiveBulkProduct(data: IToggleActiveBulkProductDTO) {
    const { all, ids, ...rest } = data;
    return httpClient.patch(ProductsEndpoints.PRODUCTS_TOGGLE_ACTIVE, all ? { all, ...rest } : { ids, ...rest });
  }

  static updateProduct({ id, data }: IUpdateProductDTO): ApiResponse<IUpdateProductResponse> {
    const formData = convertModelToFormData(data);
    return httpClient.post(`${ProductsEndpoints.PRODUCTS}/${id}`, formData);
  }

  static createProduct({ mainInfoData, filtersData }: ICreateProductDTO): ApiResponse<IUpdateProductResponse> {
    const mainInfoFormData = convertModelToFormData(mainInfoData);
    const fullFormData = convertModelToFormData(filtersData, mainInfoFormData);
    return httpClient.post(ProductsEndpoints.PRODUCTS, fullFormData);
  }

  static getProductVariations(id: string): ApiResponse<IGetProductVariationsResponse> {
    return httpClient.get(`${ProductsEndpoints.PRODUCTS}/${id}/variations`);
  }

  static deleteProductVariation(id: string) {
    return httpClient.delete(`${ProductsEndpoints.PRODUCTS}/${id}/variation`);
  }

  static addProductVariation({ product_id, variation }: IUpdateProductVariationDto) {
    return httpClient.post(`${ProductsEndpoints.PRODUCTS}/${product_id}/variation/${variation.id}`);
  }

  static bulkSearchProductVariations(
    productId: string,
    data: IBulkSearchProductVariationsDTO,
  ): ApiResponse<IBulkSearchProductVariationsResponse> {
    return httpClient.post<IBulkSearchProductVariationsResponse>(
      `${ProductsEndpoints.PRODUCTS}/${productId}/variations/bulk-search`,
      data,
    );
  }

  static addProductVariationsBulk({ product_id, variation_ids }: IAddProductVariationsBulkDTO) {
    return httpClient.post(`${ProductsEndpoints.PRODUCTS}/${product_id}/variations/bulk`, { variation_ids });
  }

  static updateProductSeo({ id, data }: { id: string; data: IUpdateProductSeoDTO }) {
    return httpClient.patch(`${ProductsEndpoints.PRODUCTS}/${id}/seo`, data);
  }
  static getProductRegionsAndPrices(data: string): ApiResponse<IGetProductRegionsAndPricesResponse> {
    return httpClient.get(`${ProductsEndpoints.PRODUCTS}/${data}/stocks`);
  }

  static updateRegionsAndPricesItem({
    id,
    product_id,
    data,
  }: {
    id: string;
    product_id: string;
    data: IUpdateRegionsAndPricesItemDTO;
  }): ApiResponse<IUpdateRegionsAndPricesItemResponse> {
    return httpClient.patch(`${ProductsEndpoints.PRODUCTS}/${product_id}/stocks/${id}`, data);
  }

  static bulkUpdateStocks(data: IBulkUpdateStocksDTO): ApiResponse<IBulkUpdateStocksResponse> {
    return httpClient.patch(ProductsEndpoints.PRODUCTS_STOCKS_BULK, data);
  }

  static getStoresList(data: IGetStoreListDTO): ApiResponse<IStoreResponse> {
    return httpClient.get<IStoreResponse>(ProductsEndpoints.STORES, { params: data });
  }

  static generateNamePreview(id: string): ApiResponse<IGenerateNamePreviewResponse> {
    return httpClient.post<IGenerateNamePreviewResponse>(`${ProductsEndpoints.PRODUCTS}/${id}/generate-name-preview`);
  }

  static translateField({
    productId,
    field,
    text,
  }: {
    productId: string;
    field: string;
    text: string;
  }): ApiResponse<ITranslateFieldResponse> {
    return httpClient.post<ITranslateFieldResponse>(`${ProductsEndpoints.PRODUCTS}/${productId}/translate-field`, {
      field,
      text,
    });
  }
}

export default ProductsAPI;
